import React, { useEffect, useState } from 'react';
import { Circle, Wifi, WifiOff } from 'lucide-react';

interface StatusBarGlobalProps {
  className?: string;
}

const FALLBACK_VERSION = '1.0.0';

function getUpdateLabel(updateState: DesktopUpdateState | null): string | null {
  if (!updateState || !updateState.supported) return null;

  switch (updateState.status) {
    case 'checking':
      return 'Verificando atualizacao';
    case 'available':
      return 'Atualizacao disponivel';
    case 'downloading':
      return 'Baixando atualizacao';
    case 'downloaded':
      return 'Atualizacao pronta';
    case 'error':
      return 'Falha no update';
    default:
      return null;
  }
}

export const StatusBarGlobal: React.FC<StatusBarGlobalProps> = ({ className = '' }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState<'synced' | 'syncing' | 'offline'>('synced');
  const [runtimeInfo, setRuntimeInfo] = useState<DesktopRuntimeInfo | null>(null);
  const [updateState, setUpdateState] = useState<DesktopUpdateState | null>(null);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setSyncStatus('synced');
    };
    const handleOffline = () => {
      setIsOnline(false);
      setSyncStatus('offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!window.electronAPI) return;

    let cancelled = false;

    window.electronAPI.getRuntimeInfo().then((info) => {
      if (!cancelled) {
        setRuntimeInfo(info);
      }
    }).catch((error) => {
      console.warn('[StatusBarGlobal] Falha ao carregar runtime info:', error);
    });

    window.electronAPI.getUpdateStatus().then((status) => {
      if (!cancelled) {
        setUpdateState(status);
      }
    }).catch((error) => {
      console.warn('[StatusBarGlobal] Falha ao carregar update status:', error);
    });

    const unsubscribe = window.electronAPI.onUpdateStatus((status) => {
      if (!cancelled) {
        setUpdateState(status);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  const isDev = import.meta.env.MODE !== 'production';
  const channelLabel = runtimeInfo?.channel === 'beta' ? 'Beta' : null;
  const updateLabel = getUpdateLabel(updateState);
  const versionLabel = `v${runtimeInfo?.version || updateState?.version || FALLBACK_VERSION}`;

  const syncLabel =
    syncStatus === 'syncing' ? 'Sincronizando' : syncStatus === 'offline' ? 'Offline' : 'Sincronizado';

  return (
    <footer
      className={`
        enterprise-statusbar-surface enterprise-font
        flex items-center justify-between shrink-0
        px-4
        text-[10px] select-none
        ${className}
      `}
      style={{ width: '100vw', height: 'var(--shell-statusbar-height, 24px)', color: 'var(--statusbar-text)' }}
    >
      <div className="flex items-center gap-3">
        <span className={`inline-flex items-center gap-1 ${isOnline ? 'text-enterprise-text-primary dark:text-slate-300' : 'text-enterprise-text-secondary dark:text-slate-500'}`}>
          {isOnline ? <Wifi size={9} /> : <WifiOff size={9} />}
          <span className="font-normal">{isOnline ? 'Online' : 'Offline'}</span>
        </span>
        <span className="inline-flex items-center gap-1 text-enterprise-text-secondary dark:text-slate-500">
          <Circle size={5} className="fill-current" />
          <span className="font-normal">{syncLabel}</span>
        </span>
      </div>

      <div className="flex items-center gap-3">
        <span className="font-normal text-enterprise-text-muted dark:text-slate-600">{versionLabel}</span>
        {channelLabel && <span className="font-normal text-enterprise-text-secondary dark:text-slate-500">{channelLabel}</span>}
        {updateLabel && <span className="font-normal text-enterprise-text-secondary dark:text-slate-500">{updateLabel}</span>}
      </div>
    </footer>
  );
};

export default StatusBarGlobal;
